<template>
  <span v-text="formatted" />
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount, defineProps, defineEmits, defineOptions } from 'vue'

// Helper function to format seconds into HH:MM:SS or MM:SS format
function formatSeconds(secs) {
  function pad(n) {
    return (n < 10 ? `0${n}` : n)
  }

  const h = Math.floor(secs / 3600)
  const m = Math.floor(secs / 60) - (h * 60)
  const s = Math.floor(secs - h * 3600 - m * 60)

  if (h > 0) {
    return `${pad(h)}:${pad(m)}:${pad(s)}`
  }

  return `${pad(m)}:${pad(s)}`
}

// Define props
const props = defineProps({
  duration: {
    type: Number,
    required: true,
  },
  seconds: {
    type: Boolean,
    default: false,
  },
})

// Define emits
const emit = defineEmits(['tick', 'finish'])

// Reactive state
const value = ref(0)
let interval = null

// Computed property for formatted output
const formatted = computed(() => {
  if (props.seconds) return Math.floor(value.value)
  return formatSeconds(value.value)
})

// Helper method to initialize the timer
const initTimer = (val) => {
  if (!val) return

  if (interval) {
    clearInterval(interval)
  }

  value.value = val
  if (value.value <= 0) return

  interval = setInterval(() => {
    value.value -= 1
    emit('tick', value.value, val)

    if (value.value <= 0) {
      clearInterval(interval)
      emit('finish')
    }
  }, 1e3)
}

// Watch for changes to duration prop
watch(() => props.duration, (newVal) => {
  initTimer(newVal)
}, { immediate: true })

// Cleanup interval when component is unmounted
onBeforeUnmount(() => {
  clearInterval(interval)
})

// Component name for debugging
defineOptions({
  name: 'Countdown'
})
</script>
